import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { 
  Mail, Send, Sparkles, Settings, Plus, Trash2, Edit2, Eye, 
  Users, CheckCircle, Clock, AlertCircle, Star, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';

const CAMPAIGN_TYPES = {
  seasonal: { label: 'Seasonal Tips', icon: Sparkles, color: 'text-amber-400' },
  review_request: { label: 'Review Request', icon: Star, color: 'text-yellow-400' },
  manual: { label: 'Custom Email', icon: Mail, color: 'text-electric-teal' },
};

const STATUS_STYLES = {
  draft: { label: 'Draft', bg: 'bg-white/10', text: 'text-white/60' },
  scheduled: { label: 'Scheduled', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  sent: { label: 'Sent', bg: 'bg-green-500/20', text: 'text-green-400' },
  sending: { label: 'Sending...', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  failed: { label: 'Failed', bg: 'bg-red-500/20', text: 'text-red-400' },
};

const ReachoutTab = () => {
  const [activeView, setActiveView] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [campaignsRes, settingsRes] = await Promise.all([
        api.admin.getReachoutCampaigns(),
        api.admin.getReachoutSettings(),
      ]);
      setCampaigns(campaignsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load reachout data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="reachout-loading">
        <div className="animate-pulse text-white/40">Loading Customer Reachout...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reachout-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-electric-teal" />
            Customer Reachout
          </h2>
          <p className="text-white/50 text-sm mt-1">Seasonal tips, review requests & custom emails</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setActiveView('settings')}
            variant="outline"
            size="sm"
            className="border-white/20 text-white/60 hover:text-white"
            data-testid="reachout-settings-btn"
          >
            <Settings className="w-4 h-4 mr-1" /> Settings
          </Button>
        </div>
      </div>

      {activeView === 'settings' && (
        <SettingsView 
          settings={settings} 
          onBack={() => setActiveView('campaigns')} 
          onSave={loadData} 
        />
      )}

      {activeView === 'campaigns' && !selectedCampaign && (
        <CampaignList 
          campaigns={campaigns} 
          onSelect={setSelectedCampaign} 
          onRefresh={loadData} 
        />
      )}

      {activeView === 'campaigns' && selectedCampaign && (
        <CampaignDetail 
          campaign={selectedCampaign} 
          settings={settings}
          onBack={() => { setSelectedCampaign(null); loadData(); }} 
          onRefresh={loadData} 
        />
      )}
    </div>
  );
};


// ==================== CAMPAIGN LIST ====================

const CampaignList = ({ campaigns, onSelect, onRefresh }) => {
  const [generating, setGenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const handleGenerate = async (type) => {
    setGenerating(true);
    try {
      const res = await api.admin.generateReachoutCampaign(type);
      toast.success('Campaign generated! Review it before sending.');
      onSelect(res.data);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate campaign');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await api.admin.deleteReachoutCampaign(id);
      toast.success('Campaign deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-4">
      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={() => handleGenerate('seasonal')}
          disabled={generating}
          className="bg-secondary-dark border border-amber-500/20 rounded-lg p-4 text-left hover:border-amber-500/40 transition-colors group"
          data-testid="generate-seasonal-btn"
        >
          <Sparkles className="w-5 h-5 text-amber-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-white font-medium text-sm">Seasonal Tips</p>
          <p className="text-white/40 text-xs mt-1">AI-generated home tips</p>
        </button>
        <button
          onClick={() => handleGenerate('review_request')}
          disabled={generating}
          className="bg-secondary-dark border border-yellow-500/20 rounded-lg p-4 text-left hover:border-yellow-500/40 transition-colors group"
          data-testid="generate-review-btn"
        >
          <Star className="w-5 h-5 text-yellow-400 mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-white font-medium text-sm">Review Request</p>
          <p className="text-white/40 text-xs mt-1">Ask for Google reviews</p>
        </button>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-secondary-dark border border-electric-teal/20 rounded-lg p-4 text-left hover:border-electric-teal/40 transition-colors group"
          data-testid="create-manual-btn"
        >
          <Plus className="w-5 h-5 text-electric-teal mb-2 group-hover:scale-110 transition-transform" />
          <p className="text-white font-medium text-sm">Custom Email</p>
          <p className="text-white/40 text-xs mt-1">Write your own message</p>
        </button>
      </div>

      {generating && (
        <div className="bg-secondary-dark border border-electric-teal/30 rounded-lg p-6 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-electric-teal border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-white/60 text-sm">Generating campaign with AI...</p>
        </div>
      )}

      {/* Campaign List */}
      <div className="space-y-2">
        <h3 className="text-white/60 text-xs font-medium uppercase tracking-wider">All Campaigns ({campaigns.length})</h3>
        {campaigns.length === 0 ? (
          <div className="bg-secondary-dark rounded-lg p-8 text-center border border-white/5">
            <Mail className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/30 text-sm">No campaigns yet. Create your first one above.</p>
          </div>
        ) : (
          campaigns.map((campaign) => {
            const type = CAMPAIGN_TYPES[campaign.campaign_type] || CAMPAIGN_TYPES.manual;
            const status = STATUS_STYLES[campaign.status] || STATUS_STYLES.draft;
            return (
              <div
                key={campaign.id}
                onClick={() => onSelect(campaign)}
                className="bg-secondary-dark rounded-lg border border-white/10 p-4 hover:border-white/20 transition-colors cursor-pointer group"
                data-testid={`campaign-${campaign.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <type.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${type.color}`} />
                    <div className="min-w-0">
                      <p className="text-white font-medium text-sm truncate">{campaign.subject || 'Untitled'}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-white/40 text-xs">{type.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                          {status.label}
                        </span>
                        {campaign.sent_count > 0 && (
                          <span className="text-white/40 text-xs flex items-center gap-1">
                            <Users className="w-3 h-3" /> {campaign.sent_count}/{campaign.recipient_count}
                          </span>
                        )}
                        {campaign.ai_generated && (
                          <span className="text-white/30 text-xs">AI</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-white/30 text-xs hidden sm:block">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </span>
                    {campaign.status === 'draft' && (
                      <Button
                        onClick={(e) => handleDelete(e, campaign.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500/50 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Manual Campaign Dialog */}
      <CreateCampaignDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(campaign) => {
          setShowCreate(false);
          onSelect(campaign);
          onRefresh();
        }}
      />
    </div>
  );
};


// ==================== CREATE CAMPAIGN DIALOG ====================

const CreateCampaignDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    subject: '',
    content: '',
    recipient_filter: 'all',
    campaign_type: 'manual',
  });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.admin.createReachoutCampaign(form);
      toast.success('Campaign created');
      onCreated(res.data);
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-secondary-dark border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-electric-teal">Create Custom Email</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-white/60">Subject Line *</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="bg-primary-dark border-white/20 text-white mt-1"
              placeholder="e.g., Important Update from Island Coastal Electric"
              data-testid="campaign-subject-input"
            />
          </div>
          <div>
            <Label className="text-white/60">Email Content (HTML supported)</Label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="bg-primary-dark border-white/20 text-white mt-1 min-h-[200px] font-mono text-sm"
              placeholder="Write your email content here... Use {{CUSTOMER_NAME}} for personalization."
              data-testid="campaign-content-input"
            />
            <p className="text-white/30 text-xs mt-1">Use {'{{CUSTOMER_NAME}}'} to personalize. HTML is supported.</p>
          </div>
          <div>
            <Label className="text-white/60">Send To</Label>
            <Select value={form.recipient_filter} onValueChange={(v) => setForm({ ...form, recipient_filter: v })}>
              <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1" data-testid="campaign-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="residential">Residential Only</SelectItem>
                <SelectItem value="commercial">Commercial Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-white/20 text-white/60">Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-electric-teal text-primary-dark font-bold"
              data-testid="create-campaign-submit"
            >
              {saving ? 'Creating...' : 'Create Draft'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ==================== CAMPAIGN DETAIL ====================

const CampaignDetail = ({ campaign, settings, onBack, onRefresh }) => {
  const [data, setData] = useState(campaign);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ subject: campaign.subject, content: campaign.content, recipient_filter: campaign.recipient_filter });
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (data.status === 'sent') {
      api.admin.getReachoutCampaignLogs(data.id).then(r => setLogs(r.data)).catch(() => {});
    }
  }, [data.id, data.status]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.admin.updateReachoutCampaign(data.id, editForm);
      setData(res.data);
      setEditing(false);
      toast.success('Campaign updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(`Send this campaign to all matching customers? This cannot be undone.`)) return;

    if (data.campaign_type === 'review_request' && !settings.google_review_link) {
      toast.error('Set your Google Review link in Settings first');
      return;
    }

    setSending(true);
    try {
      const res = await api.admin.sendReachoutCampaign(data.id);
      toast.success(res.data.message || 'Campaign is being sent!');
      setData({ ...data, status: 'sent', recipient_count: res.data.recipient_count });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const type = CAMPAIGN_TYPES[data.campaign_type] || CAMPAIGN_TYPES.manual;
  const status = STATUS_STYLES[data.status] || STATUS_STYLES.draft;
  const isDraft = data.status === 'draft';

  return (
    <div className="space-y-4">
      {/* Back */}
      <button
        onClick={onBack}
        className="text-white/40 hover:text-white text-sm flex items-center gap-1 transition-colors"
        data-testid="campaign-back-btn"
      >
        <ChevronLeft className="w-4 h-4" /> All Campaigns
      </button>

      {/* Header Card */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <type.icon className={`w-5 h-5 mt-0.5 ${type.color}`} />
            <div>
              <h3 className="text-white font-bold text-lg">{data.subject || 'Untitled Campaign'}</h3>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-white/40 text-xs">{type.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                  {status.label}
                </span>
                {data.ai_generated && <span className="text-white/30 text-xs">AI Generated</span>}
                <span className="text-white/30 text-xs">{new Date(data.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {isDraft && (
              <>
                <Button
                  onClick={() => { setEditing(true); setEditForm({ subject: data.subject, content: data.content, recipient_filter: data.recipient_filter }); }}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white/60 hover:text-white"
                  data-testid="campaign-edit-btn"
                >
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending}
                  size="sm"
                  className="bg-electric-teal text-primary-dark font-bold"
                  data-testid="campaign-send-btn"
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> {sending ? 'Sending...' : 'Send Now'}
                </Button>
              </>
            )}
            <Button
              onClick={() => setShowPreview(true)}
              variant="outline"
              size="sm"
              className="border-white/20 text-white/60 hover:text-white"
              data-testid="campaign-preview-btn"
            >
              <Eye className="w-3.5 h-3.5 mr-1" /> Preview
            </Button>
          </div>
        </div>

        {/* Stats for sent campaigns */}
        {data.status === 'sent' && (
          <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
            <div className="text-center">
              <p className="text-white font-bold text-lg">{data.recipient_count}</p>
              <p className="text-white/40 text-xs">Recipients</p>
            </div>
            <div className="text-center">
              <p className="text-green-400 font-bold text-lg">{data.sent_count}</p>
              <p className="text-white/40 text-xs">Delivered</p>
            </div>
            <div className="text-center">
              <p className="text-red-400 font-bold text-lg">{data.recipient_count - data.sent_count}</p>
              <p className="text-white/40 text-xs">Failed</p>
            </div>
          </div>
        )}
      </div>

      {/* Content Preview */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
        <h4 className="text-white/60 text-xs font-medium uppercase tracking-wider mb-3">Email Content</h4>
        <div
          className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content || '<p style="color:#999;">No content yet</p>') }}
          data-testid="campaign-content-preview"
        />
      </div>

      {/* Send Logs for sent campaigns */}
      {data.status === 'sent' && logs.length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
          <h4 className="text-white/60 text-xs font-medium uppercase tracking-wider mb-3">
            Send Log ({logs.length} recipients)
          </h4>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {logs.map((log) => (
              <div key={log.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-white/5 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {log.status === 'sent' ? (
                    <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  )}
                  <span className="text-white/80 truncate">{log.customer_name}</span>
                  <span className="text-white/30 text-xs truncate hidden sm:inline">{log.customer_email}</span>
                </div>
                <span className="text-white/30 text-xs flex-shrink-0">
                  {new Date(log.sent_at).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="bg-secondary-dark border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-electric-teal">Edit Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-white/60">Subject</Label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1"
                data-testid="edit-campaign-subject"
              />
            </div>
            <div>
              <Label className="text-white/60">Content (HTML)</Label>
              <Textarea
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1 min-h-[250px] font-mono text-sm"
                data-testid="edit-campaign-content"
              />
            </div>
            <div>
              <Label className="text-white/60">Recipients</Label>
              <Select value={editForm.recipient_filter} onValueChange={(v) => setEditForm({ ...editForm, recipient_filter: v })}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="residential">Residential Only</SelectItem>
                  <SelectItem value="commercial">Commercial Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(false)} className="border-white/20 text-white/60">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-electric-teal text-primary-dark font-bold">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-white border-none max-w-2xl max-h-[85vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 bg-gray-100 border-b">
            <DialogTitle className="text-gray-900 font-medium">{data.subject}</DialogTitle>
          </DialogHeader>
          <div
            className="p-0"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content || '<p style="padding:40px;text-align:center;color:#999;">No content</p>') }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== SETTINGS VIEW ====================

const SettingsView = ({ settings, onBack, onSave }) => {
  const [form, setForm] = useState({
    google_review_link: settings.google_review_link || '',
    schedule_months: (settings.schedule_months || [1, 4, 7, 10]).join(', '),
    auto_campaigns_enabled: settings.auto_campaigns_enabled !== false,
    reply_to: settings.reply_to || '',
  });
  const [saving, setSaving] = useState(false);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const handleSave = async () => {
    setSaving(true);
    try {
      const months = form.schedule_months
        .split(',')
        .map(m => parseInt(m.trim()))
        .filter(m => m >= 1 && m <= 12);

      await api.admin.updateReachoutSettings({
        google_review_link: form.google_review_link,
        schedule_months: months.length > 0 ? months : [1, 4, 7, 10],
        auto_campaigns_enabled: form.auto_campaigns_enabled,
        reply_to: form.reply_to,
      });
      toast.success('Reachout settings saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-white/40 hover:text-white text-sm flex items-center gap-1 transition-colors"
        data-testid="settings-back-btn"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Campaigns
      </button>

      <div className="bg-secondary-dark rounded-lg border border-white/10 p-6 max-w-xl">
        <h3 className="text-white font-bold mb-6 flex items-center gap-2">
          <Settings className="w-4 h-4 text-electric-teal" /> Reachout Settings
        </h3>

        <div className="space-y-5">
          <div>
            <Label className="text-white/60">Google Review Link</Label>
            <Input
              value={form.google_review_link}
              onChange={(e) => setForm({ ...form, google_review_link: e.target.value })}
              className="bg-primary-dark border-white/20 text-white mt-1"
              placeholder="https://g.page/r/your-business/review"
              data-testid="settings-review-link"
            />
            <p className="text-white/30 text-xs mt-1">Used in review request emails. Get this from Google Business Profile.</p>
          </div>

          <div>
            <Label className="text-white/60">Auto-Campaign Schedule (Month Numbers)</Label>
            <Input
              value={form.schedule_months}
              onChange={(e) => setForm({ ...form, schedule_months: e.target.value })}
              className="bg-primary-dark border-white/20 text-white mt-1"
              placeholder="1, 4, 7, 10"
              data-testid="settings-schedule-months"
            />
            <p className="text-white/30 text-xs mt-1">
              Comma-separated months (1-12). Current: {form.schedule_months.split(',').map(m => monthNames[parseInt(m.trim()) - 1] || '?').join(', ')}
            </p>
          </div>

          <div>
            <Label className="text-white/60">Reply-To Email (Optional)</Label>
            <Input
              value={form.reply_to}
              onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
              className="bg-primary-dark border-white/20 text-white mt-1"
              placeholder="info@islandcoastalelectric.ca"
              data-testid="settings-reply-to"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setForm({ ...form, auto_campaigns_enabled: !form.auto_campaigns_enabled })}
              className={`w-10 h-5 rounded-full transition-colors relative ${form.auto_campaigns_enabled ? 'bg-electric-teal' : 'bg-white/20'}`}
              data-testid="settings-auto-toggle"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.auto_campaigns_enabled ? 'left-5' : 'left-0.5'}`} />
            </button>
            <Label className="text-white/60">Auto-generate seasonal campaigns</Label>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full bg-electric-teal text-primary-dark font-bold" data-testid="settings-save-btn">
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReachoutTab;
